import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

/**
 * Password input with a show/hide toggle. Drop-in replacement for
 * {@code <TextField type="password" />}: same props, same look, plus an eye
 * icon in the trailing adornment that flips the field between {@code text}
 * and {@code password}. Local state only — never persists the visible state
 * across mounts.
 */
type Props = Omit<TextFieldProps, 'type'>;

export const PasswordField = forwardRef<HTMLInputElement, Props>(function PasswordField(
  { InputProps, ...rest },
  ref,
) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <TextField
      {...rest}
      inputRef={ref}
      type={visible ? 'text' : 'password'}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
              onClick={() => setVisible((v) => !v)}
              edge="end"
              size="small"
              tabIndex={-1}
            >
              {visible ? (
                <VisibilityOffOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
});
